import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter, useSensor, useSensors, MouseSensor, TouchSensor, type DragEndEvent, type Modifier } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ComponentProps } from 'react';
import type { AlbumSpread } from '../../types';
import { spreadNames, type SpreadName } from '../../utils/albumSpreads';
import AlbumSpreadView from './AlbumSpreadView';
import { Icon, Plus, Trash2 } from '../ui/Icon';

type ViewProps = Omit<ComponentProps<typeof AlbumSpreadView>, 'spread' | 'compact' | 'selectedPageId' | 'selectedIndex' | 'onSelectPage' | 'pageHandlers'>;

interface Props extends ViewProps {
  spreads: AlbumSpread[];
  selectedId: string | null;
  onSelect: (spreadId: string) => void;
  /** Every inner spread's id in the new order (ADR-0052: only those move). */
  onReorder: (spreadIds: string[]) => void;
  onAdd: () => void;
  /** Asks first: the editor confirms before anything is deleted. */
  onDelete: (spreadId: string) => void;
}

// The strip is a row: a thumbnail only moves along it.
const lockToListAxis: Modifier = ({ transform }) => ({ ...transform, y: 0 });

/** The book's spreads as thumbnails; the inner ones reorder by dragging (press and hold on touch). */
export default function AlbumSpreadList({ spreads, selectedId, onSelect, onReorder, onAdd, onDelete, ...view }: Props) {
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );
  const names = spreadNames(spreads);
  const inner = spreads.filter(s => s.kind === 'INNER');
  const label = (name: SpreadName | undefined) =>
    !name ? '' : name.kind === 'cover' ? t('album.spread.cover') : name.from === name.to ? String(name.from) : `${name.from}–${name.to}`;

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const from = inner.findIndex(s => s.id === active.id);
    const to = inner.findIndex(s => s.id === over.id);
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(inner, from, to).map(s => s.id));
  }

  const thumb = (spread: AlbumSpread) => <AlbumSpreadView {...view} spread={spread} compact />;

  return (
    <div className="flex items-start gap-3 overflow-x-auto p-1.5 -m-1.5">
      {spreads.filter(s => s.kind === 'COVER' || s.kind === 'OPENING').map(s => (
        <Thumb key={s.id} label={label(names.get(s.id))} selected={s.id === selectedId} onSelect={() => onSelect(s.id)}>{thumb(s)}</Thumb>
      ))}
      <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[lockToListAxis]} onDragEnd={handleDragEnd}>
        <SortableContext items={inner.map(s => s.id)} strategy={rectSortingStrategy}>
          {inner.map(s => (
            <SortableThumb
              key={s.id}
              id={s.id}
              label={label(names.get(s.id))}
              selected={s.id === selectedId}
              onSelect={() => onSelect(s.id)}
              onDelete={() => onDelete(s.id)}
            >
              {thumb(s)}
            </SortableThumb>
          ))}
        </SortableContext>
      </DndContext>
      <button
        onClick={onAdd}
        className="shrink-0 w-24 self-stretch mb-5 flex flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-teal-400 hover:text-teal-600 text-xs font-medium cursor-pointer"
      >
        <Icon as={Plus} size={14} />
        {t('album.spread.add')}
      </button>
      {spreads.filter(s => s.kind === 'CLOSING').map(s => (
        <Thumb key={s.id} label={label(names.get(s.id))} selected={s.id === selectedId} onSelect={() => onSelect(s.id)}>{thumb(s)}</Thumb>
      ))}
    </div>
  );
}

function Thumb({ label, selected, onSelect, children }: { label: string; selected: boolean; onSelect: () => void; children: ReactNode }) {
  return (
    <div className="shrink-0 w-32">
      <button
        onClick={onSelect}
        aria-label={label}
        aria-current={selected ? 'page' : undefined}
        className={`block w-full rounded-sm cursor-pointer ${selected ? 'ring-2 ring-teal-500 ring-offset-2 dark:ring-offset-slate-900' : ''}`}
      >
        {children}
      </button>
      <p className="mt-1 text-center font-mono text-[10px] text-slate-400">{label}</p>
    </div>
  );
}

interface SortableProps {
  id: string;
  label: string;
  selected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  children: ReactNode;
}

function SortableThumb({ id, label, selected, onSelect, onDelete, children }: SortableProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`group relative shrink-0 w-32 ${isDragging ? 'opacity-60 z-10' : ''}`}
    >
      <button
        {...attributes}
        {...listeners}
        onClick={onSelect}
        aria-label={t('album.spread.pages', { pages: label })}
        aria-current={selected ? 'page' : undefined}
        className={`block w-full rounded-sm cursor-grab active:cursor-grabbing ${selected ? 'ring-2 ring-teal-500 ring-offset-2 dark:ring-offset-slate-900' : ''}`}
      >
        {children}
      </button>
      {/* Always there on the selected spread (touch has no hover); on the rest, on hover. */}
      <button
        onClick={onDelete}
        aria-label={t('album.spread.deleteN', { pages: label })}
        title={t('album.spread.delete')}
        className={`absolute top-1 right-1 w-6 h-6 items-center justify-center rounded-full bg-white/90 dark:bg-slate-800/90 shadow-sm ring-1 ring-slate-200 dark:ring-slate-700 text-slate-500 hover:text-red-500 cursor-pointer ${
          selected ? 'flex' : 'hidden group-hover:flex focus-visible:flex'
        }`}
      >
        <Icon as={Trash2} size={12} />
      </button>
      <p className="mt-1 text-center font-mono text-[10px] text-slate-400">{label}</p>
    </div>
  );
}
